import { getMemoOptions, memo, Row, RowData, RowModel, RowSelectionState, Table, TableFeature } from "@tanstack/react-table";

export const ServerSideRowSelectionAllRowKey = "__all_rows";

const cleanupRowSelection = <TData extends RowData>(table: Table<TData>, rowSelection: RowSelectionState) => {

  return rowSelection;
};

export const ServerSideRowSelection: TableFeature = {
  createTable: <TData extends RowData>(table: Table<TData>): void => {
    table.toggleAllRowsSelected = value => {
      table.setRowSelection(() => {
        value =
          typeof value !== "undefined" ? value : !table.getIsAllRowsSelected();

        if (value) {
          return {
            [ServerSideRowSelectionAllRowKey]: true,
          };
        } else {
          return {
            [ServerSideRowSelectionAllRowKey]: false,
          };
        }
      });
    };

    table.toggleAllPageRowsSelected = value => {
      table.setRowSelection(old => {
        const resolvedValue =
          typeof value !== "undefined"
            ? value
            : !table.getIsAllPageRowsSelected();

        const rowSelection: RowSelectionState = { ...old };

        table.getRowModel()
          .rows
          .forEach(row => {
            mutateRowIsSelected(rowSelection, row.id, resolvedValue, true, table);
          });

        return cleanupRowSelection(table, rowSelection);
      });
    };

    table.getSelectedRowModel = memo(
      () => [ table.getState().rowSelection, table.getCoreRowModel() ],
      (rowSelection, rowModel) => {
        if (!Object.keys(rowSelection).length) {
          return {
            rows: [],
            flatRows: [],
            rowsById: {},
          };
        }

        return selectRowsFn(table, rowModel);
      },
      getMemoOptions(table.options, "debugTable", "getSelectedRowModel"),
    );

    table.getFilteredSelectedRowModel = memo(
      () => [ table.getState().rowSelection, table.getFilteredRowModel() ],
      (rowSelection, rowModel) => {
        if (!Object.keys(rowSelection).length) {
          return {
            rows: [],
            flatRows: [],
            rowsById: {},
          };
        }

        return selectRowsFn(table, rowModel);
      },
      getMemoOptions(table.options, "debugTable", "getFilteredSelectedRowModel"),
    );

    table.getGroupedSelectedRowModel = memo(
      () => [ table.getState().rowSelection, table.getSortedRowModel() ],
      (rowSelection, rowModel) => {
        if (!Object.keys(rowSelection).length) {
          return {
            rows: [],
            flatRows: [],
            rowsById: {},
          };
        }

        return selectRowsFn(table, rowModel);
      },
      getMemoOptions(table.options, "debugTable", "getGroupedSelectedRowModel"),
    );

    table.getIsAllRowsSelected = () => {
      const { rowSelection } = table.getState();

      return rowSelection[ServerSideRowSelectionAllRowKey] ?? false;
    };

    table.getIsAllPageRowsSelected = () => {
      const paginationFlatRows = table
        .getPaginationRowModel()
        .flatRows
        .filter(row => row.getCanSelect());
      const { rowSelection } = table.getState();

      let isAllPageRowsSelected = !!paginationFlatRows.length;

      if (rowSelection[ServerSideRowSelectionAllRowKey]) {
        if (
          isAllPageRowsSelected &&
          paginationFlatRows.some(row => rowSelection[row.id] === false)
        ) {
          isAllPageRowsSelected = false;
        }
      } else {
        if (
          isAllPageRowsSelected &&
          paginationFlatRows.some(row => !rowSelection[row.id])
        ) {
          isAllPageRowsSelected = false;
        }
      }

      return isAllPageRowsSelected;
    };

    table.getIsSomeRowsSelected = () => {
      const { rowSelection } = table.getState();
      const allRowCount = table.getRowCount();

      const totalSelected = Object.entries(rowSelection ?? {})
        .filter(([ key, value ]) => key !== ServerSideRowSelectionAllRowKey)
        .filter(([ key, value ]) => value === true)
        .length;

      const totalUnselected = Object.entries(rowSelection ?? {})
        .filter(([ key, value ]) => key !== ServerSideRowSelectionAllRowKey)
        .filter(([ key, value ]) => value === false)
        .length;


      return (
        totalSelected > 0 &&
        totalSelected < allRowCount
      ) || (
        rowSelection[ServerSideRowSelectionAllRowKey] &&
        totalUnselected > 0 &&
        totalUnselected < allRowCount
      );
    };
  },

  createRow: <TData extends RowData>(
    row: Row<TData>,
    table: Table<TData>,
  ): void => {
    row.toggleSelected = (value, opts) => {
      const isSelected = row.getIsSelected();

      table.setRowSelection(old => {
        value = typeof value !== "undefined" ? value : !isSelected;

        if (row.getCanSelect() && isSelected === value) {
          return old;
        }

        const selectedRowIds = { ...old };

        mutateRowIsSelected(
          selectedRowIds,
          row.id,
          value,
          opts?.selectChildren ?? true,
          table,
        );

        return cleanupRowSelection(table, selectedRowIds);
      });
    };

    row.getIsSelected = () => {
      const { rowSelection } = table.getState();
      return isRowSelected(row, rowSelection);
    };

    row.getIsSomeSelected = () => {
      const { rowSelection } = table.getState();
      return isSubRowSelected(row, rowSelection, table) === "some";
    };

    row.getIsAllSubRowsSelected = () => {
      const { rowSelection } = table.getState();
      return isSubRowSelected(row, rowSelection, table) === "all";
    };
  },
};


const mutateRowIsSelected = <TData extends RowData>(
  selectedRowIds: Record<string, boolean>,
  id: string,
  value: boolean,
  includeChildren: boolean,
  table: Table<TData>,
) => {
  const row = table.getRow(id, true);

  if (value) {
    if (!row.getCanMultiSelect()) {
      Object.keys(selectedRowIds)
        .forEach(key => selectedRowIds[key] = false);
    }
    if (row.getCanSelect()) {
      selectedRowIds[id] = true;
    }
  } else {
    selectedRowIds[id] = false;
  }

  if (includeChildren && row.subRows?.length && row.getCanSelectSubRows()) {
    row.subRows.forEach(row =>
      mutateRowIsSelected(selectedRowIds, row.id, value, includeChildren, table),
    );
  }
};

export function selectRowsFn<TData extends RowData>(
  table: Table<TData>,
  rowModel: RowModel<TData>,
): RowModel<TData> {
  const rowSelection = table.getState().rowSelection;

  const newSelectedFlatRows: Row<TData>[] = [];
  const newSelectedRowsById: Record<string, Row<TData>> = {};

  // Filters top level and nested rows
  const recurseRows = (rows: Row<TData>[], depth = 0): Row<TData>[] => {
    return rows
      .map(row => {
        const isSelected = isRowSelected(row, rowSelection);

        if (isSelected) {
          newSelectedFlatRows.push(row);
          newSelectedRowsById[row.id] = row;
        }

        if (row.subRows?.length) {
          row = {
            ...row,
            subRows: recurseRows(row.subRows, depth + 1),
          };
        }

        if (isSelected) {
          return row;
        }
      })
      .filter(Boolean) as Row<TData>[];
  };

  return {
    rows: recurseRows(rowModel.rows),
    flatRows: newSelectedFlatRows,
    rowsById: newSelectedRowsById,
  };
}

export function isRowSelected<TData extends RowData>(
  row: Row<TData>,
  selection: Record<string, boolean>,
): boolean {
  return selection[ServerSideRowSelectionAllRowKey] ? (selection[row.id] !== false) : (selection[row.id] ?? false);
}

export function isSubRowSelected<TData extends RowData>(
  row: Row<TData>,
  selection: Record<string, boolean>,
  table: Table<TData>,
): boolean | "some" | "all" {
  if (!row.subRows?.length) return false;

  let allChildrenSelected = true;
  let someSelected = false;

  row.subRows.forEach(subRow => {
    // Bail out early if we know both of these
    if (someSelected && !allChildrenSelected) {
      return;
    }

    if (subRow.getCanSelect()) {
      if (isRowSelected(subRow, selection)) {
        someSelected = true;
      } else {
        allChildrenSelected = false;
      }
    }

    // Check row selection of nested subrows
    if (subRow.subRows && subRow.subRows.length) {
      const subRowChildrenSelected = isSubRowSelected(subRow, selection, table);
      if (subRowChildrenSelected === "all") {
        someSelected = true;
      } else if (subRowChildrenSelected === "some") {
        someSelected = true;
        allChildrenSelected = false;
      } else {
        allChildrenSelected = false;
      }
    }
  });

  return allChildrenSelected ? "all" : someSelected ? "some" : false;
}